import React, { CSSProperties } from "react";

import {
    BackgroundFieldValue,
    colorCSS,
    getColorsFromBackgroundField,
    radius
} from "../../styles";

interface BlockProps extends React.HTMLAttributes<HTMLDivElement> {
    appearance?: string;
    background?: string;
    backgroundField?: BackgroundFieldValue;
    border?: boolean;
    borderColor?: string;
    color?: string;
    fullHeight?: boolean;
    headingColor?: string;
    linkColor?: string;
    linkHoverColor?: string;
    roundedCorners?: string;
    shadow?: boolean;
}

export const Block: React.FC<BlockProps> = ({
    appearance,
    background,
    backgroundField,
    border,
    borderColor,
    color,
    fullHeight,
    headingColor,
    linkColor,
    linkHoverColor,
    roundedCorners,
    shadow,
    style: propStyle,
    ...props
    // eslint-disable-next-line complexity
}) => {
    const style: CSSProperties = {
        ...(propStyle || {})
    };

    // Padding based on appearance
    if ((border && borderColor) || appearance === "Medium Padding") {
        style.padding = "1.5rem";
    } else if (appearance !== "No Padding") {
        style.padding = "1.5rem 3rem";
    }

    // Full height
    if (fullHeight) {
        style.height = "100%";
    }

    // Border
    if (border && borderColor) {
        style.border = `1px solid ${borderColor}`;
    }

    // Appearance-specific styles
    switch (appearance) {
        case "Highlighted":
            style.textAlign = "left";
            style.padding = "1.5rem 2.2rem";
            break;
        case "Neutral Border":
            style.border = `1px solid ${colorCSS("N60")}`;
            break;
        case "Speech Bubble Left":
        case "Speech Bubble Right":
            style.borderRadius = radius.medium;
            style.marginBottom = "2rem";
            style.width = "max-content";
            style.maxWidth = "70%";
            style.textAlign = "left";
            style.fontWeight = "300";
            style.padding = "1rem 1.7rem";
            style.position = "relative";
            if (appearance === "Speech Bubble Left") {
                style.marginRight = "auto";
            } else {
                style.marginLeft = "auto";
            }
            break;
    }

    // Rounded corners
    if (roundedCorners) {
        style.borderRadius =
            radius[roundedCorners.toLowerCase() as keyof typeof radius];
        style.overflow = "hidden";
    }

    // Shadow
    if (shadow) {
        style.boxShadow = `0 0 1rem ${colorCSS("N900", 0.25)}`;
    }

    // Apply background colors (only plain CSS properties, not styled-components css)
    if (backgroundField) {
        const bgColors = getColorsFromBackgroundField(backgroundField);
        if (bgColors.background) {
            // Use background for gradients, background-color for solid colors
            if (backgroundField.gradient) {
                style.background = bgColors.background;
            } else {
                style.backgroundColor = bgColors.background;
            }
        }
        if (bgColors.color) {
            style.color = bgColors.color;
        }
    }

    return <div style={style} {...props} />;
};
